import type { AgentOperatorBackref } from '@kilat/shared';

/**
 * Pure, React-free view logic for the agent `/operators` list: filter the
 * already-loaded back-refs by tenant status, and sort them by recency or name.
 *
 * Kept env-agnostic (no Firestore SDK, no React, no i18n runtime) so the
 * filter/sort is unit-testable in isolation and reuses no new listener — it
 * only ever re-shapes the `AgentOperatorBackref[]` the route already holds.
 * Only i18n KEYS cross this boundary (the option constants); the route
 * resolves them via `useI18n()` so copy stays translatable.
 *
 * Timestamps are read defensively (real `Timestamp` OR the bare
 * `{ seconds, nanoseconds }` cache shape a Firestore cache read / test
 * fixture can surface) — mirrors `ledger-activity.ts` / the route's own
 * `tsMs`. Nothing here divides money or mutates its input.
 */

/** A concrete tenant status (the back-ref's `currentStatus` sans `undefined`). */
export type OperatorStatus = NonNullable<AgentOperatorBackref['currentStatus']>;

/** The status filter the segmented control can select — `'all'` plus each status. */
export type StatusFilter = 'all' | OperatorStatus;

/**
 * How the list is ordered. `'priority'` is the default — the rule-based
 * "who needs attention next" ranking (see `rankOperators`); the other three
 * are the plain recency/name sorts. `'priority'` needs `nowMs`, so it is NOT
 * handled by `sortOperators` (which is pure over the recency keys) — route it
 * through `applyOperatorsView` or `rankOperators`.
 */
export type SortKey = 'priority' | 'newest' | 'recentlyPaid' | 'nameAsc';

/** The recency/name sort keys `sortOperators` handles (everything but priority). */
type RecencySortKey = Exclude<SortKey, 'priority'>;

export const DEFAULT_STATUS_FILTER: StatusFilter = 'all';
/** Priority (smart order) leads — it is the load-bearing value of this screen. */
export const DEFAULT_SORT_KEY: SortKey = 'priority';

const DAY_MS = 86_400_000;

/**
 * Soft "trial ending soon" threshold, in days elapsed since signup. The operator
 * trial defaults to 14 days (`platformConfig/moneyPolicy` → `trial.days`, read by
 * `provisionTenant`; ADR-0033 made it admin-editable, seed value still 14);
 * a trial ≥10 days in is inside its final stretch. This is a UI heuristic that
 * only drives a reason chip — never a billing decision — and the back-ref carries
 * `signupAt` but not `trialEndsAt`, so we derive it from elapsed days.
 */
export const TRIAL_NUDGE_DAYS = 10;

/**
 * Filter pills, in display order. `'all'` leads (the default, no-filter view);
 * the rest mirror the lifecycle order used elsewhere in the route. Each carries
 * the i18n key for its label so no copy leaks into this pure module.
 */
export const STATUS_FILTERS: readonly {
  readonly value: StatusFilter;
  readonly labelKey: string;
}[] = [
  { value: 'all', labelKey: 'agent.operators.filter.all' },
  { value: 'active', labelKey: 'agent.operators.filter.active' },
  { value: 'trial', labelKey: 'agent.operators.filter.trial' },
  { value: 'past_due', labelKey: 'agent.operators.filter.pastDue' },
  { value: 'suspended', labelKey: 'agent.operators.filter.suspended' },
  { value: 'archived', labelKey: 'agent.operators.filter.archived' },
];

/** Sort options, in display order. `'priority'` first — it is the default. */
export const SORT_OPTIONS: readonly { readonly value: SortKey; readonly labelKey: string }[] = [
  { value: 'priority', labelKey: 'agent.operators.sort.priority' },
  { value: 'newest', labelKey: 'agent.operators.sort.newest' },
  { value: 'recentlyPaid', labelKey: 'agent.operators.sort.recentlyPaid' },
  { value: 'nameAsc', labelKey: 'agent.operators.sort.nameAsc' },
];

/** Runtime guard so a `<select>`'s `string` value narrows to `SortKey` (no cast). */
export function isSortKey(value: string): value is SortKey {
  return SORT_OPTIONS.some((o) => o.value === value);
}

/**
 * Duck-typed Firestore-Timestamp → epoch ms. Accepts a real `Timestamp`
 * (has `toMillis`) or the `{ seconds, nanoseconds }` shape a cache read / test
 * fixture surfaces. `null` / `undefined` / unrecognised → 0 (sorts last under
 * the descending recency sorts, which is exactly "nulls last").
 */
export function tsToMs(ts: unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}

/**
 * Count of back-refs per status, plus `all` (the total). An entry with a
 * missing `currentStatus` contributes only to `all` — it has no concrete
 * status bucket, so it never inflates a specific filter's count (and, per
 * `filterOperators`, only ever appears under the "All" filter).
 */
export function countsByStatus(
  list: readonly AgentOperatorBackref[],
): Record<StatusFilter, number> {
  const counts: Record<StatusFilter, number> = {
    all: list.length,
    active: 0,
    trial: 0,
    past_due: 0,
    suspended: 0,
    archived: 0,
  };
  for (const o of list) {
    if (o.currentStatus) counts[o.currentStatus] += 1;
  }
  return counts;
}

/**
 * Filter the list by status. `'all'` returns a fresh copy of everything
 * (including entries whose `currentStatus` is missing). A specific status
 * returns only exact matches — a missing status is NOT coerced to any bucket,
 * so it shows up solely under "All" (never crashes, never mis-buckets).
 * Never mutates the input.
 */
export function filterOperators(
  list: readonly AgentOperatorBackref[],
  statusFilter: StatusFilter,
): AgentOperatorBackref[] {
  if (statusFilter === 'all') return list.slice();
  return list.filter((o) => o.currentStatus === statusFilter);
}

function compareBy(
  a: AgentOperatorBackref,
  b: AgentOperatorBackref,
  sortKey: RecencySortKey,
): number {
  switch (sortKey) {
    case 'newest':
      return tsToMs(b.signupAt) - tsToMs(a.signupAt);
    case 'recentlyPaid':
      // Descending by last payment; a missing `lastPaidAt` reads as 0 and thus
      // sorts after every operator who has ever paid — "nulls last".
      return tsToMs(b.lastPaidAt) - tsToMs(a.lastPaidAt);
    case 'nameAsc': {
      const an = a.tenantBusinessName?.trim() ?? '';
      const bn = b.tenantBusinessName?.trim() ?? '';
      if (an === bn) return 0;
      if (!an) return 1; // a has no name → after b
      if (!bn) return -1; // b has no name → after a
      return an.localeCompare(bn);
    }
  }
}

/**
 * Sort the list by the chosen key. Pure and STABLE: entries that compare equal
 * keep their original relative order (achieved by an index tie-break, so it
 * holds regardless of the engine's sort stability). Never mutates the input.
 */
export function sortOperators(
  list: readonly AgentOperatorBackref[],
  sortKey: RecencySortKey,
): AgentOperatorBackref[] {
  return list
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const primary = compareBy(a.item, b.item, sortKey);
      return primary !== 0 ? primary : a.index - b.index;
    })
    .map((d) => d.item);
}

// ── priority ranking (the rule-based "who to contact next" core) ──────────
//
// A DETERMINISTIC re-ordering of the already-loaded back-refs so the operators
// who need the agent's attention surface first. No AI, no backend, no cost —
// this is the load-bearing value of the "agent next-operator ranking" slice.
// `nowMs` is injected (never `Date.now()` here) so the order is reproducible
// and unit-testable.

/**
 * Priority bucket per lifecycle status — LOWER sorts first (more urgent):
 *   past_due (0) → churn risk / commission at stake, act now
 *   trial    (1) → convert before the 14-day trial lapses
 *   suspended(2) → recently lost, still-warm win-back
 *   archived (3) → dormant, cold win-back
 *   active   (4) → healthy + paying, lowest urgency
 * A missing `currentStatus` is treated as `trial` — exactly as the row UI
 * renders it (`currentStatus ?? 'trial'`), so ranking and display agree.
 */
const STATUS_PRIORITY: Record<OperatorStatus, number> = {
  past_due: 0,
  trial: 1,
  suspended: 2,
  archived: 3,
  active: 4,
};

/** The reason an operator sits where it does — a KEY the UI maps to localized
 *  copy (never English here). One per status, with `trial` split by urgency. */
export type PriorityReason =
  | 'pastDue'
  | 'trialEndingSoon'
  | 'trialActive'
  | 'winBack'
  | 'dormant'
  | 'active';

/** Reason key → i18n label key, for the reason chip that explains the order. */
export const PRIORITY_REASON_LABEL_KEYS: Record<PriorityReason, string> = {
  pastDue: 'agent.operators.priority.reason.pastDue',
  trialEndingSoon: 'agent.operators.priority.reason.trialEndingSoon',
  trialActive: 'agent.operators.priority.reason.trialActive',
  winBack: 'agent.operators.priority.reason.winBack',
  dormant: 'agent.operators.priority.reason.dormant',
  active: 'agent.operators.priority.reason.active',
};

/** Effective status for ranking/reason — mirrors the row UI's `?? 'trial'`. */
function effectiveStatus(backref: AgentOperatorBackref): OperatorStatus {
  return backref.currentStatus ?? 'trial';
}

/**
 * Why this operator is prioritised — a deterministic reason KEY (no copy).
 * `trial` splits into `trialEndingSoon` (≥`TRIAL_NUDGE_DAYS` days in) vs
 * `trialActive`. Pure: takes `nowMs`, never reads the clock.
 */
export function operatorPriorityReason(
  backref: AgentOperatorBackref,
  nowMs: number,
): PriorityReason {
  const status = effectiveStatus(backref);
  switch (status) {
    case 'past_due':
      return 'pastDue';
    case 'trial': {
      const signupMs = tsToMs(backref.signupAt);
      const days = signupMs > 0 ? Math.floor((nowMs - signupMs) / DAY_MS) : 0;
      return days >= TRIAL_NUDGE_DAYS ? 'trialEndingSoon' : 'trialActive';
    }
    case 'suspended':
      return 'winBack';
    case 'archived':
      return 'dormant';
    case 'active':
      return 'active';
  }
}

/**
 * Within-bucket urgency — HIGHER sorts first. Only ever compared between
 * operators in the SAME status bucket, so the per-status scale (a "gap" for the
 * act-now statuses vs a raw timestamp for the win-back/healthy ones) never
 * crosses. A missing timestamp reads as 0 via `tsToMs` and falls to a sensible
 * end of its bucket.
 */
function urgencyWithin(
  backref: AgentOperatorBackref,
  status: OperatorStatus,
  nowMs: number,
): number {
  const signupMs = tsToMs(backref.signupAt);
  const lastPaidMs = tsToMs(backref.lastPaidAt);
  switch (status) {
    case 'past_due':
      // Longest since last payment (or signup if never paid) = deepest into
      // arrears = most urgent → bigger gap first.
      return nowMs - (lastPaidMs > 0 ? lastPaidMs : signupMs);
    case 'trial':
      // Oldest trial = closest to the 14-day expiry = convert first.
      return nowMs - signupMs;
    case 'suspended':
    case 'archived':
    case 'active':
      // Win-back / healthy: the more recently they paid, the warmer / more
      // engaged the lead → rank first (bigger timestamp). Never-paid → signup.
      return lastPaidMs > 0 ? lastPaidMs : signupMs;
  }
}

/**
 * Rule-based priority order over the already-loaded back-refs. Deterministic
 * and STABLE: sort by status bucket, then within-bucket urgency, then business
 * name (A–Z, missing names last), then original index as an absolute tie-break.
 * Never mutates the input; never reads the clock.
 */
export function rankOperators(
  list: readonly AgentOperatorBackref[],
  nowMs: number,
): AgentOperatorBackref[] {
  return list
    .map((item, index) => ({ item, index, status: effectiveStatus(item) }))
    .sort((a, b) => {
      const bucket = STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status];
      if (bucket !== 0) return bucket;
      const urgency =
        urgencyWithin(b.item, b.status, nowMs) - urgencyWithin(a.item, a.status, nowMs);
      if (urgency !== 0) return urgency;
      const an = a.item.tenantBusinessName?.trim() ?? '';
      const bn = b.item.tenantBusinessName?.trim() ?? '';
      if (an !== bn) {
        if (!an) return 1; // a has no name → after b
        if (!bn) return -1; // b has no name → after a
        return an.localeCompare(bn);
      }
      return a.index - b.index;
    })
    .map((d) => d.item);
}

/**
 * The one entry point the route uses: filter by status, then order by the
 * chosen sort. `'priority'` runs the rule-based ranking (needs `nowMs`);
 * every other key delegates to the pure `sortOperators`. Never mutates input.
 */
export function applyOperatorsView(
  list: readonly AgentOperatorBackref[],
  opts: { readonly statusFilter: StatusFilter; readonly sortKey: SortKey; readonly nowMs: number },
): AgentOperatorBackref[] {
  const filtered = filterOperators(list, opts.statusFilter);
  if (opts.sortKey === 'priority') return rankOperators(filtered, opts.nowMs);
  return sortOperators(filtered, opts.sortKey);
}
